import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import UserMenu from "@/components/UserMenu";
import NoWorkspace from "@/components/NoWorkspace";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import Budgets from "@/pages/Budgets";
import Goals from "@/pages/Goals";
import Categories from "@/pages/Categories";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import {
  LayoutDashboard,
  ArrowLeftRight,
  PiggyBank,
  Target,
  Tags,
  Wallet,
  ShieldCheck,
} from "lucide-react";

const baseNav = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true, testId: "nav-overview" },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight, testId: "nav-transactions" },
  { to: "/budgets", label: "Budgets", icon: PiggyBank, testId: "nav-budgets" },
  { to: "/goals", label: "Goals", icon: Target, testId: "nav-goals" },
  { to: "/categories", label: "Categories", icon: Tags, testId: "nav-categories" },
];

function Shell({ children }) {
  const { user, activeCompanyId } = useAuth();
  const navItems = [...baseNav];
  if (user?.role === "admin") {
    navItems.push({ to: "/admin", label: "Admin", icon: ShieldCheck, testId: "nav-admin" });
  }

  return (
    <div className="App min-h-screen bg-[#FBFBF9] text-[#212421]">
      <header className="sticky top-0 z-30 bg-[#FBFBF9]/85 backdrop-blur border-b border-[#EAE8E3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-[#1F4E3D] flex items-center justify-center">
              <Wallet className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-display text-lg font-medium tracking-tight">Kosh</span>
          </div>
          <nav className="flex items-center gap-1 flex-1 justify-center" data-testid="primary-nav">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.testId}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-md text-sm transition-colors flex items-center gap-1.5 ${
                    isActive
                      ? "bg-[#1F4E3D] text-white"
                      : "text-[#696E69] hover:text-[#212421] hover:bg-[#F3F1ED]"
                  }`
                }
              >
                <it.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="hidden md:inline">{it.label}</span>
              </NavLink>
            ))}
          </nav>
          <UserMenu />
        </div>
      </header>
      <main
        key={activeCompanyId}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-up"
      >
        {children}
      </main>
      <footer className="border-t border-[#EAE8E3] mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-[#696E69] flex justify-between">
          <span className="label-overline">Kosh — A grounded ledger</span>
          <span className="font-mono-num">v1.1</span>
        </div>
      </footer>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Shell>
              <LedgerRoutes />
            </Shell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function LedgerRoutes() {
  const { activeCompanyId } = useAuth();
  // If user has no workspace yet (and isn't on /admin), show placeholder
  return (
    <Routes>
      <Route
        path="/"
        element={activeCompanyId ? <Dashboard /> : <NoWorkspace />}
      />
      <Route
        path="/transactions"
        element={activeCompanyId ? <Transactions /> : <NoWorkspace />}
      />
      <Route
        path="/budgets"
        element={activeCompanyId ? <Budgets /> : <NoWorkspace />}
      />
      <Route
        path="/goals"
        element={activeCompanyId ? <Goals /> : <NoWorkspace />}
      />
      <Route
        path="/categories"
        element={activeCompanyId ? <Categories /> : <NoWorkspace />}
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
      <Toaster richColors position="top-right" />
    </BrowserRouter>
  );
}

export default App;
